import { NextApiResponse } from 'next';
import getConfig from 'next/config';
import { ServerResponse } from 'http';
import urlJoin from 'url-join';

import { ServerClientContext } from './clientContext.js';

interface RuntimeConfig {
  ENV: string;
  IDENTITY_API_URL: string;
}

export const getRuntimeConfig = (): RuntimeConfig | undefined => {
  const config = getConfig() as { publicRuntimeConfig?: RuntimeConfig } | undefined;
  return config?.publicRuntimeConfig;
};

type RequestOptions = RequestInit & {
  method?: 'GET' | 'POST' | 'PUT' | 'DELETE' | 'PATCH';
  credentials?: 'include' | 'same-origin' | 'omit';
};

export interface ErrorResponse {
  error: {
    message: string;
    showCallCenter: boolean;
    showResetPassword: boolean;
    field?: string;
    accountRestricted?: boolean;
  };
  userLoggedIn: boolean;
  clientContext: ServerClientContext;
  showCaptcha: boolean;
  displayClientSelector: boolean;
}

export class FetchError extends Error {
  constructor(
    message: string,
    public readonly clientContext: ServerClientContext | undefined,
    public readonly userLoggedIn: boolean,
    public readonly statusCode: number,
    public readonly showCallCenter: boolean = false,
    public readonly showResetPassword: boolean = false,
    public readonly field: string | undefined = undefined,
    public readonly showCaptcha: boolean = false,
    public readonly displayClientSelector: boolean = false,
    public readonly accountRestricted: boolean = false,
  ) {
    super(message);
  }
}

export const withIdentityApiUrl = (suffix: string): string => {
  const config = getRuntimeConfig();
  if (config?.ENV === 'local') {
    return urlJoin(config.IDENTITY_API_URL, suffix);
  }
  return suffix;
};

export interface ServerSideContext {
  res?: NextApiResponse | ServerResponse;
}

export const fetchWrapper = async <T = any>(
  url: string,
  options: RequestOptions = {},
  context?: ServerSideContext,
): Promise<T> => {
  const headers = {
    'Content-Type': 'application/json',
    Accept: 'application/json',
    ...options.headers,
  };

  const res = await fetch(url, {
    ...options,
    headers,
  });

  const setCookieHeaders = res.headers?.getSetCookie?.() ?? [];
  if (setCookieHeaders.length > 0 && context?.res) {
    const passedCookies = setCookieHeaders.filter(
      (cookie) => cookie.startsWith('client-id=') || cookie.startsWith('haven-mobile-login='),
    );

    if (passedCookies.length > 0) {
      context.res.setHeader('Set-Cookie', passedCookies);
    }
  }

  if (res.ok) {
    if (res.status === 204) return {} as T;
    const payload = (await res.json()) as T;
    return payload;
  } else {
    if (res.status === 404) {
      throw new FetchError(res.statusText, undefined, false, res.status, undefined);
    }
    const status = res?.status || 500;
    const error = (await res.json()) as ErrorResponse;
    throw new FetchError(
      error.error ? error.error.message : res.statusText,
      error.clientContext,
      error.userLoggedIn || false,
      status,
      error.error?.showCallCenter || false,
      error.error?.showResetPassword || false,
      error.error?.field,
      error.showCaptcha,
      error.displayClientSelector,
      error.error?.accountRestricted || false,
    );
  }
};
